import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as AiIcons from 'react-icons/ai';

import { IReserva } from '../../types/Reserva';
import api from '../../services/api';

import Context, { IContext } from '../../context/Context';

import ModalReserva from '../ModalReserva';
import Paginacao from '../CadastroGenerico/Paginacao';

interface ITabelaReservas {
  codusu?: number;
  datres?: string;
}

type StatusAceite = 'aguardando' | 'aceite' | 'conferido';

// Busca sem diferenciar maiúsculas/minúsculas e acentos
function normalizarBusca(texto: string) {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function statusAceite(reserva: IReserva): StatusAceite {
  if (reserva.acesai) return 'conferido';
  if (reserva.aceent) return 'aceite';
  return 'aguardando';
}

const chipsStatus: Record<StatusAceite, { texto: string, classe: string }> = {
  aguardando: { texto: 'Aguardando aceite', classe: 'bg-zinc-100 text-zinc-600' },
  aceite: { texto: 'Em uso', classe: 'bg-amber-50 text-amber-800' },
  conferido: { texto: 'Conferido', classe: 'bg-emerald-50 text-emerald-700' },
};

function formatarDataReserva(datres: string) {
  const data = parse(datres, 'dd/MM/yyyy', new Date());
  return isNaN(data.getTime()) ? datres : format(data, "EEE, dd/MM/yyyy", { locale: ptBR });
}

const classeBotaoSecundario = 'flex h-10 items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-900 transition-colors hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900';
const classeBotaoPrimario = 'flex h-10 items-center justify-center gap-1.5 rounded-xl bg-zinc-900 px-3 text-sm font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400';

export default function TabelaReservas({ codusu, datres }: ITabelaReservas) {
  const navigate = useNavigate();
  const { caminho } = useParams();
  const { setIsLoadingOverlay }: IContext = useContext(Context);

  const [reservas, setReservas] = useState<IReserva[]>([]);
  const [reservaEditando, setReservaEditando] = useState<IReserva | undefined>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [busca, setBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);

  const ehPortaria = !!datres;
  // Mesma regra de "hoje" usada no botão de termos (data em UTC)
  const hojeFormatado = new Date().toISOString().substring(0, 10).split('-').reverse().join('/');

  async function getReservas() {
    try {
      setIsLoadingOverlay(true);
      const response = await api.get('/reservas', {
        params: {
          datres: datres,
          codusu: codusu,
          sta: 'PAGO',
          ordem: 'DESC'
        }
      });

      if (response.status === 200) {
        if (datres) {
          setReservas(response.data.sort((a: IReserva, b: IReserva) => a.espaco.esp.localeCompare(b.espaco.esp)));
          return;
        }

        setReservas(response.data);
      }
    } catch (error: any) {
      if (error.response?.data) return toast.error(error.response.data?.erro);
      toast.error('Erro ao buscar reservas');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  function abrirModal(reserva: IReserva) {
    setReservaEditando(reserva);
    setModalVisible(true);
  }

  useEffect(() => {
    getReservas();
  }, []);

  const termoBusca = normalizarBusca(busca.trim());
  const reservasFiltradas = reservas.filter(reserva => !termoBusca || normalizarBusca([
    reserva.datres,
    reserva.espaco.tipesp.tipesp,
    reserva.espaco.esp,
    reserva.tipres?.des,
    !codusu ? reserva.usuario?.usu : '',
  ].join(' ')).includes(termoBusca));

  const totalPaginas = Math.max(1, Math.ceil(reservasFiltradas.length / itensPorPagina));
  const paginaExibida = Math.min(paginaAtual, totalPaginas);
  const reservasDaPagina = reservasFiltradas.slice((paginaExibida - 1) * itensPorPagina, paginaExibida * itensPorPagina);

  const totalConferidas = reservas.filter(reserva => reserva.acesai).length;

  function botoesAcao(reserva: IReserva) {
    const ehResponsavel = codusu === reserva.usuario.cod;

    return (
      <>
        <button
          type='button'
          onClick={() => navigate(`/${caminho}/reservaDetalhes/${reserva.cod}`)}
          className={classeBotaoSecundario}
        >
          <AiIcons.AiOutlineTeam className='h-4 w-4' aria-hidden='true' />
          Convidados
        </button>
        {(ehResponsavel && reserva.datres === hojeFormatado) &&
          <button
            type='button'
            onClick={() => abrirModal(reserva)}
            disabled={Boolean(reserva?.aceent)}
            className={classeBotaoPrimario}
          >
            {reserva?.aceent ?
              <><AiIcons.AiFillCheckCircle className='h-4 w-4' aria-hidden='true' />Termo aceito</> :
              <><AiIcons.AiOutlineFileText className='h-4 w-4' aria-hidden='true' />Termos de uso</>}
          </button>}
        {(!ehResponsavel && reserva?.aceent) &&
          <button
            type='button'
            onClick={() => abrirModal(reserva)}
            disabled={Boolean(reserva?.acesai)}
            className={reserva?.acesai ?
              'flex h-10 cursor-not-allowed items-center justify-center gap-1.5 rounded-xl bg-emerald-50 px-3 text-sm font-semibold text-emerald-700' :
              classeBotaoPrimario}
          >
            {reserva?.acesai ?
              <><AiIcons.AiFillCheckCircle className='h-4 w-4' aria-hidden='true' />Conferido</> :
              <><AiIcons.AiOutlineAudit className='h-4 w-4' aria-hidden='true' />Conferência</>}
          </button>}
      </>
    );
  }

  function chipStatus(reserva: IReserva) {
    const status = chipsStatus[statusAceite(reserva)];

    return (
      <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${status.classe}`}>
        {status.texto}
      </span>
    );
  }

  return (
    <div className='w-full max-w-5xl mx-auto px-4 pb-10'>
      {reservaEditando &&
        <ModalReserva
          key={reservaEditando.cod}
          reservaEditando={reservaEditando}
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          onConfirmado={getReservas}
        />}

      <header className='mb-6 mt-4'>
        <h1 className='text-2xl font-semibold text-zinc-900 md:text-3xl'>
          {ehPortaria ? `Reservas do Dia` : 'Minhas Reservas'}
        </h1>
        <p className='mt-1 text-zinc-500' aria-live='polite'>
          {ehPortaria ?
            `${formatarDataReserva(datres!.split('-').reverse().join('/'))} · ${totalConferidas} de ${reservas.length} ${reservas.length === 1 ? 'conferida' : 'conferidas'}` :
            `${reservas.length} ${reservas.length === 1 ? 'reserva confirmada' : 'reservas confirmadas'}`}
        </p>
      </header>

      <div className='relative mb-4'>
        <AiIcons.AiOutlineSearch className='pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400' aria-hidden='true' />
        <input
          type='search'
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPaginaAtual(1);
          }}
          placeholder={ehPortaria ? 'Buscar espaço ou responsável…' : 'Buscar espaço ou data…'}
          aria-label='Buscar reservas'
          className='h-12 w-full rounded-xl border border-zinc-300 bg-white pl-11 pr-11 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 [&::-webkit-search-cancel-button]:hidden'
        />
        {busca && (
          <button
            type='button'
            onClick={() => {
              setBusca('');
              setPaginaAtual(1);
            }}
            aria-label='Limpar busca'
            className='absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            <AiIcons.AiOutlineClose className='h-4 w-4' aria-hidden='true' />
          </button>
        )}
      </div>

      <section className='overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-card'>
        {reservasFiltradas.length === 0 ? (
          <div className='flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-zinc-400'>
            <AiIcons.AiOutlineCalendar className='h-8 w-8' aria-hidden='true' />
            {termoBusca ? (
              <>
                Nenhum resultado para “{busca.trim()}”.
                <button
                  type='button'
                  onClick={() => setBusca('')}
                  className='font-semibold text-zinc-900 underline underline-offset-4 hover:text-zinc-600'
                >
                  Limpar busca
                </button>
              </>
            ) : (
              ehPortaria ? 'Nenhuma reserva para hoje.' : 'Você ainda não possui reservas confirmadas.'
            )}
          </div>
        ) : (
          <>
            <div className='hidden overflow-x-auto md:block'>
              <table className='w-full text-left text-sm'>
                <thead className='bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500'>
                  <tr>
                    <th scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>Data Reservada</th>
                    <th scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>Tipo</th>
                    <th scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>Espaço</th>
                    <th scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>Reserva</th>
                    {!codusu && <th scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>Responsável</th>}
                    {ehPortaria && <th scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>Status</th>}
                    {/* Coluna de ações fixa à direita, como no CadastroGenerico */}
                    <th scope='col' className='sticky right-0 z-10 bg-zinc-50 px-4 py-3 text-right font-medium shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]'><span className='sr-only'>Ações</span></th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-zinc-100'>
                  {reservasDaPagina.map(reserva => (
                    <tr key={reserva.cod} className='group text-zinc-700 hover:bg-zinc-50'>
                      <td className='whitespace-nowrap px-4 py-3 font-medium capitalize text-zinc-900'>
                        {formatarDataReserva(reserva.datres)}
                        {reserva.datres === hojeFormatado && !ehPortaria && (
                          <span className='ml-2 rounded-full bg-zinc-900 px-2 py-0.5 text-[11px] font-medium normal-case text-white'>Hoje</span>
                        )}
                      </td>
                      <td className='px-4 py-3'>{reserva.espaco.tipesp.tipesp}</td>
                      <td className='px-4 py-3 font-medium text-zinc-900'>{reserva.espaco.esp}</td>
                      <td className='px-4 py-3'>{reserva.tipres.des}</td>
                      {!codusu && <td className='px-4 py-3'>{reserva.usuario.usu}</td>}
                      {ehPortaria && <td className='px-4 py-3'>{chipStatus(reserva)}</td>}
                      <td className='sticky right-0 z-10 bg-white px-4 py-2 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)] group-hover:bg-zinc-50'>
                        <div className='flex items-center justify-end gap-2 whitespace-nowrap'>
                          {botoesAcao(reserva)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className='divide-y divide-zinc-100 md:hidden'>
              {reservasDaPagina.map(reserva => (
                <li key={reserva.cod} className='px-4 py-4'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0'>
                      <p className='text-xs font-medium uppercase tracking-wide text-zinc-500'>
                        {formatarDataReserva(reserva.datres)}
                      </p>
                      <p className='mt-0.5 break-words font-semibold text-zinc-900'>{reserva.espaco.esp}</p>
                    </div>
                    {ehPortaria ? chipStatus(reserva) : (reserva.datres === hojeFormatado && (
                      <span className='rounded-full bg-zinc-900 px-2.5 py-0.5 text-xs font-medium text-white'>Hoje</span>
                    ))}
                  </div>
                  <dl className='mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm'>
                    <dt className='text-zinc-500'>Tipo</dt>
                    <dd className='min-w-0 break-words text-zinc-900'>{reserva.espaco.tipesp.tipesp}</dd>
                    <dt className='text-zinc-500'>Reserva</dt>
                    <dd className='min-w-0 break-words text-zinc-900'>{reserva.tipres.des}</dd>
                    {!codusu && (
                      <>
                        <dt className='text-zinc-500'>Responsável</dt>
                        <dd className='min-w-0 break-words text-zinc-900'>{reserva.usuario.usu}</dd>
                      </>
                    )}
                  </dl>
                  <div className='mt-3 grid auto-cols-fr grid-flow-col gap-2'>
                    {botoesAcao(reserva)}
                  </div>
                </li>
              ))}
            </ul>

            {reservasFiltradas.length > 10 && (
              <Paginacao
                paginaAtual={paginaExibida}
                totalPaginas={totalPaginas}
                totalItens={reservasFiltradas.length}
                itensPorPagina={itensPorPagina}
                onMudarPagina={setPaginaAtual}
                onMudarItensPorPagina={(quantidade) => {
                  setItensPorPagina(quantidade);
                  setPaginaAtual(1);
                }}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}
